/**
 * App-owned device / session registry (Manage devices).
 * Not Cognito remembered-devices — never store biometric templates.
 */

import type { DevicePermissionReport, DeviceSetupReport } from '../setup/report'

export const MAX_TRUSTED_DEVICES = 5
export const DEVICE_STALE_INACTIVE_DAYS = 90
/**
 * A trusted install counts as an approver (can approve / remove / rename OTHER
 * devices) only if it was seen within this window. Idle trusted rows stay
 * trusted until the 90-day cleanup, but no longer block email-code recovery.
 */
export const ACTIVE_APPROVER_WINDOW_DAYS = 14

export type UserDeviceStatus = 'pending' | 'trusted' | 'revoked'

export type UserDevicePlatform = 'ios' | 'android' | 'web' | 'unknown'

export type UserDeviceRecord = {
  userId: string
  deviceId: string
  platform: UserDevicePlatform
  displayName: string
  /** True when the user has customized the auto-generated name. */
  displayNameCustom?: boolean
  appVersion?: string | null
  createdAt: string
  lastSeenAt: string
  status: UserDeviceStatus
  /** Client-reported: soft-lock / local biometric enrolled on this install. */
  biometricEnrolled: boolean
  lastIp?: string | null
  /** Coarse location label (city/region or timezone-derived); never precise GPS. */
  approxLocation?: string | null
  /** Push token / web endpoint associated with this install, when known. */
  pushTokenRef?: string | null
  revokedAt?: string | null
  /**
   * A Provider Owner signed this device (or everyone's devices) out at this
   * time: a session that signed in before it can't register or heartbeat this
   * row, so the app signs itself out. A fresh sign-in can.
   */
  sessionsEndedAt?: string | null
  approvedAt?: string | null
  approvedByDeviceId?: string | null
  /**
   * How this row became trusted: `first-install` | `auto-trust-orphan` |
   * `email-code` | `approved-by:<last 6 of the approving device id>`.
   */
  approvedVia?: string | null
  /** SHA-256 (hex) of the per-install secret issued at first claim. Never returned. */
  secretHash?: string | null
  secretIssuedAt?: string | null
  /**
   * Wrong / missing-secret requests rejected against this row. Informational
   * only: a failure never changes `status` or `secretHash`. Reset to 0 by the
   * next verified call.
   */
  secretFailureCount?: number | null
  secretFailureAt?: string | null
  /** Latest device-reported permission states (Android 134 / iOS 99+; absent on older builds). */
  permissions?: DevicePermissionReport | null
  /** Latest device-reported setup progress. */
  setup?: DeviceSetupReport | null
  /** Server time of the last accepted `permissions` / `setup` report. */
  setupReportedAt?: string | null
}

export type UserDeviceView = {
  deviceId: string
  platform: string
  displayName: string
  appVersion: string | null
  createdAt: string
  lastSeenAt: string
  status: UserDeviceStatus
  biometricEnrolled: boolean
  lastIp: string | null
  approxLocation: string | null
  approvedVia?: string | null
  permissions?: DevicePermissionReport | null
  setup?: DeviceSetupReport | null
  setupReportedAt?: string | null
  /**
   * The per-install secret, returned ONCE on the call that issues it (first
   * register/heartbeat of an unclaimed row). Null on every other response.
   */
  installSecret?: string | null
}

export type RegisterDeviceInput = {
  deviceId: string
  platform: UserDevicePlatform
  displayName: string
  appVersion?: string | null
  biometricEnrolled?: boolean | null
  approxLocation?: string | null
  pushTokenRef?: string | null
  /** The install secret this device holds (absent on the very first register). */
  deviceSecret?: string | null
  /**
   * Explicit opt-in to be issued an install secret (new clients send `true`).
   * A legacy client sends neither field and is never claimed / demoted.
   */
  claimSecret?: boolean | null
  /** AWSJSON `DevicePermissionReport`; whitelisted server-side before storing. */
  permissions?: unknown
  /** AWSJSON `DeviceSetupReport`; whitelisted server-side before storing. */
  setup?: unknown
}

export type HeartbeatDeviceInput = {
  deviceId: string
  deviceSecret?: string | null
  claimSecret?: boolean | null
  biometricEnrolled?: boolean | null
  approxLocation?: string | null
  appVersion?: string | null
  pushTokenRef?: string | null
  permissions?: unknown
  setup?: unknown
}
